import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";

const PRINCIPLES = [
  {
    n: "01",
    title: "One move, written down",
    finding:
      "Across decades of goal-setting research, specific and challenging goals reliably produce higher performance than vague intent like \"do your best\" (Locke & Latham).",
    inApp:
      "The Founders App asks for exactly one concrete move per business per day — not a task list you can hide inside.",
  },
  {
    n: "02",
    title: "When and where you'll do it",
    finding:
      "Implementation intentions — an if-then plan naming the time, place, and trigger — have been shown across hundreds of studies to substantially raise follow-through versus goal intentions alone (Gollwitzer & Sheeran).",
    inApp:
      "Every daily move has an optional \"when and where\" line. Naming 9:15am, at the desk, before email is the part that makes it happen.",
  },
  {
    n: "03",
    title: "An honest evening check",
    finding:
      "Frequent progress monitoring — especially when the outcome is physically recorded — is associated with greater goal attainment than monitoring without recording (Harkin et al., meta-analysis).",
    inApp:
      "The evening loop asks did you do it, yes or no, plus what actually happened. No streaks to protect, no guilt if the answer is no.",
  },
  {
    n: "04",
    title: "Reflection separated from execution",
    finding:
      "Structured written reflection is linked to improved performance in field studies of workers who spent minutes a day writing about their work (Di Stefano, Gino, Pisano & Staats).",
    inApp:
      "The journal is deliberately separate from the daily loop, so thinking time never competes with doing time.",
  },
  {
    n: "05",
    title: "A monthly move that scares you a little",
    finding:
      "Progress on hard, meaningful work — not comfort — is the strongest driver of motivation in day-to-day work life (Amabile & Kramer, The Progress Principle).",
    inApp:
      "On the 1st, each business gets a bold-move prompt, stored apart from daily work so you can see over a year what you were actually willing to risk.",
  },
  {
    n: "06",
    title: "Kept where someone can see it",
    finding:
      "Public commitment and periodic accountability reporting are associated with higher completion rates than private goals held alone.",
    inApp:
      "Your history is a chronological record you can read back, export, or walk a partner through — outreach, pricing, product, ops, all in one line.",
  },
];

export const Route = createFileRoute("/method")({
  head: () => ({
    meta: [
      { title: "The Method — Why The Founders App Works" },
      {
        name: "description",
        content:
          "The research-informed thinking behind The Founders App: one written move a day, an if-then plan, an honest evening check, monthly bold moves, and a readable history.",
      },
      { property: "og:title", content: "The Method — Why The Founders App Works" },
      {
        property: "og:description",
        content:
          "Goal specificity, implementation intentions, progress monitoring and reflection — built into a daily loop for founders.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://thefoundersapp.com/method" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/method" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "The Method — Why The Founders App Works",
          about: "Goal setting, implementation intentions and progress monitoring for founders",
          publisher: { "@type": "Organization", name: "Skylab Studio LLC" },
        }),
      },
    ],
  }),
  component: MethodPage,
});

function MethodPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto w-full max-w-4xl px-6 py-20">
        <p className="micro-label">The Method</p>
        <h1 className="mt-4 text-3xl font-medium leading-tight sm:text-4xl">
          Why writing one move a day moves a business
        </h1>
        <p className="mt-5 max-w-2xl leading-relaxed text-muted-foreground">
          The Founders App isn't a productivity theory we invented. It's a loop assembled from the parts of
          behavioural and organisational research that keep showing up whenever people actually
          finish what they set out to do: name the thing, name when you'll do it, check honestly
          that evening, and keep a record you can read back.
        </p>

        <div className="mt-14 space-y-6">
          {PRINCIPLES.map((p) => (
            <section key={p.n} className="soft-panel p-7">
              <p className="micro-label">{p.n}</p>
              <h2 className="mt-3 text-xl font-medium">{p.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.finding}</p>
              <p className="mt-4 border-l border-border pl-4 text-sm leading-relaxed">
                <span className="micro-label">In the app</span>
                <br />
                {p.inApp}
              </p>
            </section>
          ))}
        </div>

        <section className="vault-panel mt-14 p-7">
          <h2 className="text-lg font-medium">The honest fine print</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The Founders App has not run its own clinical or controlled scientific study, and we make no
            medical, psychological, or financial claims. The principles above come from published
            research on goal setting and behaviour change conducted by others, in settings that
            aren't identical to running a business. What we can say honestly: this formula — one
            specific move, a stated time and place, an honest evening review, and a record you keep
            — has worked consistently for the founders and operators we've watched use it, across
            very different kinds of businesses. Your results depend on the work you do, not on the
            app. The Founders App is a tool for keeping that work visible.
          </p>
        </section>

        <div className="mt-12 flex flex-wrap gap-3">
          <Button asChild className="sheen">
            <Link to="/auth">Start the loop</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/pricing">See pricing</Link>
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
